import React, {
  createContext,
  FormEvent,
  Reducer,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import * as css from './stedsection.module.scss';
import * as commonCss from '../avtalesectionstyles.module.scss';
import {
  AgreementStateActionType,
  getCssBasedOnModalHeight,
  NewPraksisstedSignatar,
  NewPraksisstedSignatarDraft,
  PraksisstedForAvtale,
  StudiestedForAvtale,
  useAgreementContext,
} from '../index';
import {
  RowBackground,
  Table,
  TableCellTh,
  TableCellTd,
  TableHeader,
  TableMain,
  TableRow,
} from '../../../components/table/alpTable';
import Button, { ArrowToggleButton } from '../../../components/button';
import { useIntl } from 'react-intl';
import studiestedTranslations from './studiestedsection.i18n';
import praksisstedTranslations from './praksisstedsection.i18n';
import classNames from 'classnames';
import { ConfirmationDialog } from '../../../components/dialog';
import {
  MinimalPraksisstedMedHierarkiDto,
  MinimalStudiestedDto,
  StudiestedbrukerDto,
  StudiestedbrukerSignatarDto,
} from '../../../api/generated/studiested-generated-api';
import { GenericActionWithPayload } from '../../../util';
import Spinner from '../../../components/spinner/spinner';
import Checkbox from '../../../components/input/checkbox';
import Icon, { IconType } from '../../../components/icon';
import {
  StudiestedSelect,
  StudiestedSigneringSection,
  StudiestedTable,
  StudiestedViewMode,
} from './studiestedsection';
import {
  PraksisstedSelect,
  PraksisstedSigneringSection,
  PraksisstedTable,
  PraksisstedViewMode,
} from './praksisstedsection';
import useDynamicDocumentAndElementHeights from '../../../hooks/useDynamicDocumentAndElementHeights';
import useContextNotUndefined from '../../../hooks/useContextNotUndefined';
import { devEnvFlag, testEnvFlag } from '../../../util/featureFlags';
import validateEmail from '../../../util/validateEmail';
import validatePhoneNo from '../../../util/validatePhoneNo';
import { InformationDialog } from '../../../components/dialog/informationDialog';
import {
  anyCheckboxesAreChecked,
  AgreementCheckboxes,
  flattenUndernoder,
  getInitCheckboxes,
  getHasAllCheckedOrIndeterminateCheckboxUndernoder,
  getHasCheckedParent,
  mapCheckedCheckboxesToIds,
  useSetCheckedState,
  getCheckedCheckboxUndernoderIds,
} from './handleCheckboxes';

type StederForAvtale = StudiestedForAvtale | PraksisstedForAvtale[];
type StedForAvtale = StudiestedForAvtale | PraksisstedForAvtale;

interface StedSectionContextProps {
  sted?: StederForAvtale;
  isStudiestedSection: boolean;
}

export const StedSectionContext = createContext<
  StedSectionContextProps | undefined
>(undefined);

export const useStedSectionContext = () =>
  useContextNotUndefined(StedSectionContext, 'useStedSectionContext');

type PraksisstedToEdit = {
  praksisstedToEdit?: PraksisstedForAvtale;
};

const PraksisstedToEditContext = createContext<PraksisstedToEdit | undefined>(
  undefined,
);

export const usePraksisstedToEditContext = () =>
  useContextNotUndefined(
    PraksisstedToEditContext,
    'usePraksisstedToEditContext',
  );

export enum Stedtype {
  STUDIESTED,
  PRAKSISSTED,
}

type PraksisstedSignatar = NewPraksisstedSignatar | NewPraksisstedSignatarDraft;

type StedSignatar =
  | StudiestedbrukerSignatarDto
  | PraksisstedSignatar
  | undefined;

const signatarHasChanged = (
  initSignatar: StedSignatar,
  signatar: StedSignatar,
  stedtype: Stedtype,
): boolean => {
  if (
    !initSignatar &&
    (!signatar || !Object.values(signatar).some((field) => field))
  ) {
    return false;
  }

  if ((!initSignatar && signatar) || (initSignatar && !signatar)) {
    return true;
  }

  if (stedtype === Stedtype.STUDIESTED) {
    return (
      (initSignatar as StudiestedbrukerSignatarDto).studiestedbrukerId !==
      (signatar as StudiestedbrukerSignatarDto).studiestedbrukerId
    );
  }

  const {
    fornavn: initFornavn,
    etternavn: initEtternavn,
    telefonnummer: initTelefonnummer,
    epost: initEpost,
  } = initSignatar as PraksisstedSignatar;

  const { fornavn, etternavn, telefonnummer, epost } =
    signatar as PraksisstedSignatar;

  return (
    (initFornavn ?? '') !== fornavn ||
    (initEtternavn ?? '') !== etternavn ||
    (initTelefonnummer ?? '') !== telefonnummer ||
    (initEpost ?? '') !== epost
  );
};

const stedForAvtaleHasHanged = (
  initSted: StedForAvtale,
  sted: StedForAvtale,
  stedtype: Stedtype,
): boolean => {
  const { id, checkedIds, signatar } = initSted;

  return (
    id !== sted.id ||
    checkedIds.length !== sted.checkedIds.length ||
    checkedIds.some((checkedId) => !sted.checkedIds.includes(checkedId)) ||
    signatarHasChanged(signatar, sted.signatar, stedtype)
  );
};

const useStudiestedHasChanged = (): ((
  studiested: StudiestedForAvtale | undefined,
) => boolean) => {
  const { initAgreement } = useAgreementContext();

  return (studiested: StudiestedForAvtale | undefined) => {
    if (!initAgreement?.studiested) {
      return !!studiested;
    }

    return (
      !studiested ||
      stedForAvtaleHasHanged(
        initAgreement.studiested,
        studiested,
        Stedtype.STUDIESTED,
      )
    );
  };
};

const usePraksisstederHasChanged = (): ((
  praksissteder: PraksisstedForAvtale[],
) => boolean) => {
  const { initAgreement } = useAgreementContext();

  return (praksissteder: PraksisstedForAvtale[]) => {
    if (!initAgreement?.praksissteder) return true;
    if (
      initAgreement.praksissteder.length === 0 &&
      praksissteder.length === 0
    ) {
      return false;
    }

    return (
      initAgreement.praksissteder.length !== praksissteder.length ||
      initAgreement.praksissteder.some((initPraksissted) => {
        const initPraksisstedFromPraksissteder = praksissteder.find(
          (praksissted) => praksissted.id === initPraksissted.id,
        );

        if (!initPraksisstedFromPraksissteder) return true;

        return stedForAvtaleHasHanged(
          initPraksissted,
          initPraksisstedFromPraksissteder,
          Stedtype.PRAKSISSTED,
        );
      })
    );
  };
};

type StedSectionProps = {
  stedtype: Stedtype;
};

const StedSection: React.FC<React.PropsWithChildren<StedSectionProps>> = ({
  stedtype,
}) => {
  const { studiested, praksissteder, dispatchAgreementEdit, canEditAgreement } =
    useAgreementContext();

  const isStudiestedSection = useMemo(
    () => stedtype === Stedtype.STUDIESTED,
    [stedtype],
  );

  const sted = useMemo(
    () => (isStudiestedSection ? studiested : praksissteder),
    [isStudiestedSection, studiested, praksissteder],
  );

  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [praksisstedToEdit, setPraksisstedToEdit] =
    useState<PraksisstedForAvtale>();

  const studiestedHasChanged = useStudiestedHasChanged();
  const praksisstederHasChanged = usePraksisstederHasChanged();

  const dispatchStudiested = (newStudiested?: StudiestedForAvtale) =>
    dispatchAgreementEdit({
      type: AgreementStateActionType.SET_STUDIESTED,
      payload: {
        content: newStudiested,
        studiestedHasChanged: studiestedHasChanged(newStudiested),
      },
    });

  const dispatchPraksissteder = (newPraksissteder?: PraksisstedForAvtale[]) =>
    dispatchAgreementEdit({
      type: AgreementStateActionType.SET_PRAKSISSTEDER,
      payload: {
        content: newPraksissteder,
        praksisstedHasChanged: praksisstederHasChanged(newPraksissteder || []),
      },
    });

  const setSted = (newSted?: StederForAvtale) =>
    isStudiestedSection
      ? dispatchStudiested(newSted as StudiestedForAvtale | undefined)
      : dispatchPraksissteder(newSted as PraksisstedForAvtale[] | undefined);

  const onClose = (newSted?: StedForAvtale, saveButton: boolean = false) => {
    if (isStudiestedSection) {
      if (saveButton) {
        setSted(newSted as StudiestedForAvtale);
      }
    } else {
      if (saveButton) {
        const newSteder = sted
          ? (sted as PraksisstedForAvtale[]).filter(
              (praksissted) => praksissted.id !== praksisstedToEdit?.id,
            )
          : [];

        setSted(newSteder.concat(newSted as PraksisstedForAvtale));
      }
    }

    setModalIsOpen(false);
  };

  const onRemovePraksissted = (praksissted: PraksisstedForAvtale) => {
    const steder = sted as PraksisstedForAvtale[] | undefined;

    if (steder) {
      const newSteder = steder.filter(
        (existingSted) => existingSted.id !== praksissted.id,
      );
      setSted(newSteder.length > 0 ? newSteder : undefined);
    }
  };

  const stedSectionContexValue = useMemo<StedSectionContextProps>(
    () => ({ sted, isStudiestedSection }),
    [sted, isStudiestedSection],
  );

  const praksisstedToEditContextValue = useMemo<PraksisstedToEdit>(
    () => ({ praksisstedToEdit }),
    [praksisstedToEdit],
  );

  return (
    <StedSectionContext.Provider value={stedSectionContexValue}>
      <PraksisstedToEditContext.Provider value={praksisstedToEditContextValue}>
        <div
          id={isStudiestedSection ? 'studiestedSection' : 'praksisstedSection'}
        >
          {sted &&
            (isStudiestedSection ? (
              <StudiestedTable sted={sted as StudiestedForAvtale} />
            ) : (
              <PraksisstedTable
                steder={sted as PraksisstedForAvtale[]}
                setPraksisstedToEdit={(praksissted) => {
                  setPraksisstedToEdit(praksissted);
                  setModalIsOpen(true);
                }}
                removePraksissted={onRemovePraksissted}
              />
            ))}
          {canEditAgreement ? (
            <ActionButtons
              setSted={setSted}
              onClick={() => {
                setPraksisstedToEdit(undefined);
                setModalIsOpen(true);
              }}
            />
          ) : (
            <ViewModeActionButtons
              onClick={() => {
                setPraksisstedToEdit(undefined);
                setModalIsOpen(true);
              }}
            />
          )}
          {modalIsOpen && <StedSectionModal onClose={onClose} isOpen />}
        </div>
      </PraksisstedToEditContext.Provider>
    </StedSectionContext.Provider>
  );
};

interface ViewModeActionButtonsProps {
  onClick: () => void;
}

const ViewModeActionButtons: React.FC<
  React.PropsWithChildren<ViewModeActionButtonsProps>
> = ({ onClick }) => {
  const { sted, isStudiestedSection } = useStedSectionContext();
  const { formatMessage } = useIntl();

  const translations = useMemo(
    () =>
      isStudiestedSection ? studiestedTranslations : praksisstedTranslations,
    [isStudiestedSection],
  );

  const labels = useMemo(
    () => ({
      showSted: formatMessage({ id: translations.showSted.id }),
    }),
    [formatMessage, translations],
  );

  if (!isStudiestedSection) return <></>;

  return (
    <div
      className={classNames(commonCss.actionBtns, sted && commonCss.btnMargin)}
    >
      <Button.Secondary
        data-cy={
          isStudiestedSection ? 'show-studiested-btn' : 'show-praksissted-btn'
        }
        className={commonCss.button}
        onClick={onClick}
      >
        {labels.showSted}
      </Button.Secondary>
    </div>
  );
};

interface ActionButtonsProps {
  setSted: (sted?: StudiestedForAvtale) => void;
  onClick: () => void;
}

const ActionButtons: React.FC<React.PropsWithChildren<ActionButtonsProps>> = ({
  setSted,
  onClick,
}) => {
  const { sted, isStudiestedSection } = useStedSectionContext();
  const { formatMessage } = useIntl();

  const translations = useMemo(
    () =>
      isStudiestedSection ? studiestedTranslations : praksisstedTranslations,
    [isStudiestedSection],
  );

  const labels = useMemo(
    () => ({
      addSted: formatMessage({ id: translations.addSted.id }),
      editSted: formatMessage({ id: translations.editSted.id }),
      removeSted: formatMessage({ id: translations.removeSted.id }),
    }),
    [formatMessage, translations],
  );

  return (
    <div
      className={classNames(commonCss.actionBtns, sted && commonCss.btnMargin)}
    >
      {sted && isStudiestedSection && (
        <Button.Tertiary
          data-cy={'remove-studiested-btn'}
          className={commonCss.button}
          onClick={() => setSted()}
        >
          {labels.removeSted}
        </Button.Tertiary>
      )}
      <Button.Secondary
        className={commonCss.button}
        data-cy={
          isStudiestedSection ? 'add-studiested-btn' : 'add-praksissted-btn'
        }
        onClick={onClick}
      >
        {sted && isStudiestedSection ? labels.editSted : labels.addSted}
      </Button.Secondary>
    </div>
  );
};

interface ModalProps {
  onClose: (sted?: StedForAvtale, saveButton?: boolean) => void;
  isOpen: boolean;
}

export type MinimalSted =
  | MinimalStudiestedDto
  | MinimalPraksisstedMedHierarkiDto;

type ModalState = {
  toppnode: MinimalSted | undefined;
  checkboxes: AgreementCheckboxes;
  signatar: StedSignatar;
};

enum ModalStateActionType {
  SET_TOPPNODE,
  SET_CHECKED_IDS,
  SET_SIGNATOER,
  SET_ALL,
}

type ModalStateAction =
  | GenericActionWithPayload<
      ModalStateActionType.SET_TOPPNODE,
      MinimalSted | undefined
    >
  | GenericActionWithPayload<
      ModalStateActionType.SET_CHECKED_IDS,
      AgreementCheckboxes
    >
  | GenericActionWithPayload<
      ModalStateActionType.SET_SIGNATOER,
      | StudiestedbrukerDto
      | NewPraksisstedSignatar
      | NewPraksisstedSignatarDraft
      | undefined
    >
  | GenericActionWithPayload<ModalStateActionType.SET_ALL, ModalState>;

const initModalState: ModalState = {
  checkboxes: {},
  signatar: undefined,
  toppnode: undefined,
};

const modalStateReducer: Reducer<ModalState, ModalStateAction> = (
  state,
  action,
) => {
  switch (action.type) {
    case ModalStateActionType.SET_TOPPNODE:
      return {
        ...state,
        toppnode: action.payload,
        checkboxes: action.payload
          ? getInitCheckboxes(action.payload.noder.under, [])
          : {},
      };
    case ModalStateActionType.SET_CHECKED_IDS:
      return { ...state, checkboxes: action.payload };
    case ModalStateActionType.SET_SIGNATOER:
      return { ...state, signatar: action.payload };
    case ModalStateActionType.SET_ALL:
      return action.payload;
  }

  return state;
};

export type SelectedStedContextProps = {
  toppnode: MinimalSted | undefined;
  flattenedToppnodeUndernoder: MinimalSted[];
  checkboxes: AgreementCheckboxes;
  setToppnode: (sted: MinimalSted | undefined) => void;
  setCheckboxes: (checkboxes: AgreementCheckboxes) => void;
};

const SelectedStedContext = createContext<SelectedStedContextProps | undefined>(
  undefined,
);

export const useSelectedStedContext = () =>
  useContextNotUndefined(SelectedStedContext, 'useSelectedStedContext');

const StedSectionModal: React.FC<React.PropsWithChildren<ModalProps>> = ({
  onClose,
  isOpen,
}) => {
  const { canEditAgreement } = useAgreementContext();
  const { sted: inheritedStedBeforeType, isStudiestedSection } =
    useStedSectionContext();
  const { praksisstedToEdit } = usePraksisstedToEditContext();
  const inheritedSted = useMemo(
    () =>
      isStudiestedSection
        ? (inheritedStedBeforeType as StudiestedForAvtale | undefined)
        : praksisstedToEdit,
    [isStudiestedSection, inheritedStedBeforeType, praksisstedToEdit],
  );

  const [state, dispatch] = useReducer(modalStateReducer, initModalState);

  useEffect(() => {
    if (inheritedSted) {
      const { signatar: inheritedSignatar, checkedIds: inheritedCheckedIds } =
        inheritedSted;

      const allStederCheckedState = getInitCheckboxes(
        inheritedSted.noder.under,
        inheritedCheckedIds,
      );

      dispatch({
        type: ModalStateActionType.SET_ALL,
        payload: {
          toppnode: inheritedSted,
          signatar: inheritedSignatar,
          checkboxes: allStederCheckedState,
        },
      });
    }
  }, [inheritedSted]);

  const { signatar, checkboxes, toppnode } = state;

  const translations = isStudiestedSection
    ? studiestedTranslations
    : praksisstedTranslations;

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      addSted: formatMessage({ id: translations.addSted.id }),
      cancel: formatMessage({ id: translations.cancel.id }),
      modalTitle: formatMessage({ id: translations.modalTitle.id }),
      modalSubtitle: formatMessage({
        id: translations.modalSubtitle.id,
      }),
      modalSubtitleView: formatMessage({
        id: translations.modalSubtitleView.id,
      }),
    }),
    [formatMessage, translations],
  );

  const close = (sendSted: boolean) => {
    if (sendSted && toppnode) {
      onClose(
        isStudiestedSection
          ? ({
              ...toppnode,
              signatar,
              checkedIds: mapCheckedCheckboxesToIds(checkboxes),
            } as StudiestedForAvtale)
          : ({
              ...toppnode,
              signatar,
              checkedIds: mapCheckedCheckboxesToIds(checkboxes),
            } as PraksisstedForAvtale),
        true,
      );
    } else {
      onClose();
    }
  };

  const setSignatar = (
    bruker:
      | StudiestedbrukerDto
      | NewPraksisstedSignatar
      | NewPraksisstedSignatarDraft
      | undefined,
  ) => {
    bruker !== signatar &&
      dispatch({
        type: ModalStateActionType.SET_SIGNATOER,
        payload: bruker,
      });
  };

  const isSubmitDisabled = useMemo(() => {
    if (isStudiestedSection) {
      // If there is a studiestedsignatar, all fields should have values

      return (
        !toppnode ||
        (signatar &&
          (!signatar?.fornavn ||
            !signatar?.etternavn ||
            !signatar?.telefonnummer ||
            !signatar?.epost))
      );
    }

    // If there is a praksisstedsignatar, fields are voluntary, but email/phone need to follow valid patterns
    if (!signatar) return !toppnode;

    const { epost, telefonnummer } = signatar;
    const validEmail = !epost || validateEmail(epost);
    const validPhoneNo = !telefonnummer || validatePhoneNo(telefonnummer);

    return !validEmail || !validPhoneNo || !toppnode;
  }, [signatar, toppnode, isStudiestedSection]);

  // Used mainly to include Enter key submit of signering form
  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (!isSubmitDisabled) {
      close(true);
    }
  };

  const formId = !isStudiestedSection
    ? 'praksissted-signering-post'
    : undefined;

  const {
    documentHeight,
    elementHeight: modalHeight,
    elementRef: modalContentRef,
  } = useDynamicDocumentAndElementHeights();

  const flattenedToppnodeUndernoder: MinimalSted[] = useMemo(
    () => (toppnode ? flattenUndernoder(toppnode.noder.under) : []),
    [toppnode],
  );

  const selectedStedContextValue = useMemo<SelectedStedContextProps>(() => {
    const setToppnode = (newSted: MinimalSted | undefined) => {
      dispatch({
        type: ModalStateActionType.SET_TOPPNODE,
        payload: newSted,
      });
    };

    const setCheckboxes = (ids: AgreementCheckboxes) => {
      dispatch({
        type: ModalStateActionType.SET_CHECKED_IDS,
        payload: ids,
      });
    };

    return {
      toppnode,
      checkboxes,
      setToppnode,
      setCheckboxes,
      flattenedToppnodeUndernoder,
    };
  }, [toppnode, checkboxes, flattenedToppnodeUndernoder, dispatch]);

  if (!canEditAgreement) {
    return (
      <InformationDialog
        extraClasses={[commonCss.modal]}
        contentCss={getCssBasedOnModalHeight({ documentHeight, modalHeight })}
        isOpen={isOpen}
        title={toppnode?.navn}
        subtitle={labels.modalSubtitleView}
        onClose={() => close(false)}
      >
        <SelectedStedContext.Provider value={selectedStedContextValue}>
          <div ref={modalContentRef}>
            <StedViewMode />
          </div>
        </SelectedStedContext.Provider>
      </InformationDialog>
    );
  }

  return (
    <ConfirmationDialog
      extraClasses={[commonCss.modal]}
      contentCss={getCssBasedOnModalHeight({ documentHeight, modalHeight })}
      isOpen={isOpen}
      title={labels.modalTitle}
      subtitle={labels.modalSubtitle}
      submitLabel={labels.addSted}
      cancelLabel={labels.cancel}
      onClose={close}
      submitDisabled={isSubmitDisabled}
      formId={formId}
    >
      <SelectedStedContext.Provider value={selectedStedContextValue}>
        <div ref={modalContentRef}>
          <StedSelect>
            {toppnode && canEditAgreement && (devEnvFlag || testEnvFlag) && (
              <>
                {isStudiestedSection ? (
                  <StudiestedSigneringSection
                    signatar={signatar as StudiestedbrukerDto}
                    setSignatar={setSignatar}
                    toppnode={toppnode as MinimalStudiestedDto}
                  />
                ) : (
                  <PraksisstedSigneringSection
                    signatar={signatar as NewPraksisstedSignatar}
                    setSignatar={setSignatar}
                    submit={submit}
                    formId={formId!}
                  />
                )}
              </>
            )}
          </StedSelect>
        </div>
      </SelectedStedContext.Provider>
    </ConfirmationDialog>
  );
};

const StedViewMode = () => {
  const { isStudiestedSection } = useStedSectionContext();

  return isStudiestedSection ? <StudiestedViewMode /> : <PraksisstedViewMode />;
};

const StedSelect: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { isStudiestedSection } = useStedSectionContext();
  return (
    <>
      {isStudiestedSection ? (
        <StudiestedSelect>{children}</StudiestedSelect>
      ) : (
        <PraksisstedSelect>{children}</PraksisstedSelect>
      )}
    </>
  );
};

export enum ModalTableHeader {
  STED = 'STED',
  STATUS = 'STATUS',
  HANDLING = 'HANDLING',
}

export const TABLE_LOADER_HEIGHT = '2.5em';

export const ModalTable = () => {
  const { canEditAgreement } = useAgreementContext();
  const { toppnode, checkboxes, flattenedToppnodeUndernoder } =
    useSelectedStedContext();
  const { isStudiestedSection } = useStedSectionContext();
  const { formatMessage } = useIntl();
  const setCheckedState = useSetCheckedState();

  const translations = isStudiestedSection
    ? studiestedTranslations
    : praksisstedTranslations;

  const maxNoElements = isStudiestedSection ? 8 : 6;

  const allUndernoderAreChecked = useMemo(
    () =>
      getHasAllCheckedOrIndeterminateCheckboxUndernoder(
        flattenedToppnodeUndernoder,
        checkboxes,
      ),
    [checkboxes, flattenedToppnodeUndernoder],
  );

  const labels = useMemo(
    () => ({
      modalNotOmfattetSteder: formatMessage({
        id: translations.modalNotOmfattetSteder.id,
      }),
      modalOmfattetSteder: formatMessage({
        id: translations.modalOmfattetSteder.id,
      }),
    }),
    [formatMessage, translations],
  );

  const tableHeaders = useMemo(
    () =>
      // TODO Remove, when omfattedStudiested is supported
      !(devEnvFlag || testEnvFlag) && isStudiestedSection
        ? {
            [ModalTableHeader.STED]: formatMessage({
              id: translations.modalStedHeader.id,
            }),
          }
        : {
            [ModalTableHeader.STED]: formatMessage({
              id: translations.modalStedHeader.id,
            }),
            [ModalTableHeader.STATUS]: formatMessage({
              id: translations.modalStatusHeader.id,
            }),
            [ModalTableHeader.HANDLING]: formatMessage({
              id: translations.modalHandlingHeader.id,
            }),
          },
    [formatMessage, translations, isStudiestedSection],
  );

  const headers = useMemo(
    () =>
      Object.entries(tableHeaders).map(([type, title]) => (
        <TableCellTh
          key={type}
          aria-label={type === ModalTableHeader.HANDLING ? title : undefined}
          align={type === ModalTableHeader.HANDLING ? 'right' : undefined}
          className={
            type === ModalTableHeader.HANDLING
              ? css.selectAllCheckbox
              : undefined
          }
        >
          {type === ModalTableHeader.HANDLING ? (
            <Checkbox
              id={'select-all-checkbox'}
              setChecked={() =>
                toppnode &&
                setCheckedState(toppnode.id, allUndernoderAreChecked)
              }
              checked={allUndernoderAreChecked}
              disabled={!canEditAgreement}
            />
          ) : (
            title
          )}
        </TableCellTh>
      )),
    [
      tableHeaders,
      canEditAgreement,
      allUndernoderAreChecked,
      setCheckedState,
      toppnode,
    ],
  );

  const rows = useMemo(
    () => <ModalTableRows headers={tableHeaders} />,
    [tableHeaders],
  );

  const hasOmfattetUndernoder = useMemo(
    () => Object.values(checkboxes).some(({ checked }) => checked),
    [checkboxes],
  );

  if (toppnode && Object.keys(checkboxes).length === 0)
    return <div style={{ height: TABLE_LOADER_HEIGHT }} />;

  return toppnode ? (
    <>
      <Table maxNoElements={maxNoElements}>
        <TableHeader>
          <TableRow top>{headers}</TableRow>
        </TableHeader>
        <TableMain>{rows}</TableMain>
      </Table>
      {
        /* TODO Remove, when omfattedStudiested is supported */
        (devEnvFlag || testEnvFlag || !isStudiestedSection) && (
          <p>
            <strong>
              {hasOmfattetUndernoder
                ? labels.modalOmfattetSteder
                : labels.modalNotOmfattetSteder}
            </strong>
          </p>
        )
      }
    </>
  ) : (
    <Spinner height={TABLE_LOADER_HEIGHT} />
  );
};

type StudiestedModalTableHeaders = {
  [ModalTableHeader.STED]: string;
};

type PraksisstedModalTableHeaders = {
  [ModalTableHeader.STATUS]: string;
  [ModalTableHeader.STED]: string;
  [ModalTableHeader.HANDLING]: string;
};

type ModalTableHeaders =
  | PraksisstedModalTableHeaders
  | StudiestedModalTableHeaders;

const mapUndernoderToTableRows = (
  undernoder: MinimalSted[],
  checkboxes: AgreementCheckboxes,
  setChecked: (checkedId: string) => void,
  headers: ModalTableHeaders,
  getStatus: (undernode: MinimalSted) => RowStatusKey,
  depth: number = 1,
): JSX.Element[] =>
  undernoder.reduce(
    (acc: JSX.Element[], undernode: MinimalSted) =>
      acc
        .concat(
          <ModalTableRow
            key={undernode.id}
            sted={undernode}
            indent={depth}
            checked={checkboxes[undernode.id].checked}
            indeterminate={checkboxes[undernode.id].indeterminate}
            setChecked={setChecked}
            headers={headers}
            status={getStatus(undernode)}
          />,
        )
        .concat(
          mapUndernoderToTableRows(
            undernode.noder.under,
            checkboxes,
            setChecked,
            headers,
            getStatus,
            depth + 1,
          ),
        ),
    [],
  );

const useGetModalTableRowStatus = (): ((
  undernode: MinimalSted,
) => RowStatusKey) => {
  const { isStudiestedSection } = useStedSectionContext();
  const { toppnode, checkboxes, flattenedToppnodeUndernoder } =
    useSelectedStedContext();

  const anyCheckedCheckboxes = useMemo(
    () => anyCheckboxesAreChecked(checkboxes),
    [checkboxes],
  );

  const erroneousCheckboxIdsInHierarchy = useMemo(
    () =>
      flattenedToppnodeUndernoder.reduce((acc: string[], node: MinimalSted) => {
        const {
          id,
          noder: { under },
        } = node;

        if (acc.includes(id)) return acc;

        if (checkboxes[id].checked) {
          const flattenedUndernoder = flattenUndernoder(under);
          const checkedUndernodeIds = getCheckedCheckboxUndernoderIds(
            flattenedUndernoder,
            checkboxes,
          );

          if (checkedUndernodeIds.length > 0) {
            return [
              ...acc,
              id,
              ...flattenedUndernoder.map((undernode) => undernode.id),
            ];
          }
        }

        return acc;
      }, []),
    [flattenedToppnodeUndernoder, checkboxes],
  );

  return (undernode) => {
    if (isStudiestedSection || !anyCheckedCheckboxes)
      return RowStatus.AKTIV_IKKE_BESTILLINGSPUNKT;

    const { id } = undernode;

    if (erroneousCheckboxIdsInHierarchy.includes(id)) {
      return RowStatus.UGYLDIG_BESTILLINGSPUNKTER_I_HIERARKI;
    }

    const { checked, indeterminate } = checkboxes[id];

    if (checked) return RowStatus.AKTIV_BESTILLINGSPUNKT;
    if (indeterminate) return RowStatus.AKTIV_IKKE_BESTILLINGSPUNKT;

    const undernodeHasCheckedParent = getHasCheckedParent(
      flattenedToppnodeUndernoder.find((node) => node.id === id)!,
      checkboxes,
      toppnode,
    );

    if (undernodeHasCheckedParent) {
      return RowStatus.AKTIV_IKKE_BESTILLINGSPUNKT;
    }

    return RowStatus.INAKTIV_IKKE_MED_I_AVTALE;
  };
};

interface ModalTableRowsProps {
  headers: ModalTableHeaders;
}

const ModalTableRows: React.FC<
  React.PropsWithChildren<ModalTableRowsProps>
> = ({ headers }) => {
  const { toppnode, checkboxes } = useSelectedStedContext();
  const undernoder = (toppnode?.noder.under || []) as MinimalSted[];
  const [collapsed, setCollapsed] = useState<{ [id: string]: boolean }>(
    undernoder.reduce((acc, node) => ({ ...acc, [node.id]: false }), {}),
  );

  const setCheckedState = useSetCheckedState();
  const getStatus = useGetModalTableRowStatus();

  const handleCheckedRow = (checkedId: string) => {
    setCheckedState(checkedId);
  };

  const handleCollapsedChange = (shouldCollapse: boolean, id: string) =>
    setCollapsed({ ...collapsed, [id]: shouldCollapse });

  return (
    <>
      {undernoder.map((undernode) => {
        const { checked, indeterminate } = checkboxes[undernode.id];

        return (
          <React.Fragment key={undernode.id}>
            <ModalTableRow
              sted={undernode}
              collapsed={collapsed[undernode.id]}
              setCollapsed={handleCollapsedChange}
              checked={checked}
              indeterminate={indeterminate}
              setChecked={handleCheckedRow}
              headers={headers}
              status={getStatus(undernode)}
              top
            />
            {!collapsed[undernode.id] &&
              mapUndernoderToTableRows(
                undernode.noder.under,
                checkboxes,
                handleCheckedRow,
                headers,
                getStatus,
              )}
          </React.Fragment>
        );
      })}
    </>
  );
};

const RowStatus = {
  AKTIV_BESTILLINGSPUNKT: 'AKTIV_BESTILLINGSPUNKT',
  AKTIV_IKKE_BESTILLINGSPUNKT: 'AKTIV_IKKE_BESTILLINGSPUNKT',
  INAKTIV_IKKE_MED_I_AVTALE: 'INAKTIV_IKKE_MED_I_AVTALE',
  UGYLDIG_BESTILLINGSPUNKTER_I_HIERARKI:
    'UGYLDIG_BESTILLINGSPUNKTER_I_HIERARKI',
} as const;

// Exported for use in tests
export type RowStatusKey = keyof typeof RowStatus;

interface CommonRowProps {
  sted: MinimalSted;
  checked: boolean;
  indeterminate: boolean;
  setChecked: (checkedId: string) => void;
  headers: ModalTableHeaders;
  status: RowStatusKey;
}

interface TopRowProps extends CommonRowProps {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean, id: string) => void;
  top: true;
  indent?: never;
}

interface RowProps extends CommonRowProps {
  indent: number;
  collapsed?: never;
  setCollapsed?: never;
  top?: never;
}

type ModalTableRowProps = TopRowProps | RowProps;

const useStatusFieldLabel = (status: RowStatusKey): string => {
  const { isStudiestedSection } = useStedSectionContext();

  const { formatMessage } = useIntl();
  const translations = isStudiestedSection
    ? studiestedTranslations
    : praksisstedTranslations;

  const labels: { [status in RowStatusKey]: string } = useMemo(
    () => ({
      AKTIV_BESTILLINGSPUNKT: formatMessage({
        id: translations.AKTIV_BESTILLINGSPUNKT.id,
      }),
      AKTIV_IKKE_BESTILLINGSPUNKT: formatMessage({
        id: translations.AKTIV_IKKE_BESTILLINGSPUNKT.id,
      }),
      INAKTIV_IKKE_MED_I_AVTALE: formatMessage({
        id: translations.INAKTIV_IKKE_MED_I_AVTALE.id,
      }),
      UGYLDIG_BESTILLINGSPUNKTER_I_HIERARKI: formatMessage({
        id: translations.UGYLDIG_BESTILLINGSPUNKTER_I_HIERARKI.id,
      }),
    }),
    [formatMessage, translations],
  );

  return labels[status];
};

const getTableRowBackground = (
  status: RowStatusKey,
): keyof typeof RowBackground => {
  switch (status) {
    case RowStatus.UGYLDIG_BESTILLINGSPUNKTER_I_HIERARKI:
      return RowBackground.alert;
    case RowStatus.INAKTIV_IKKE_MED_I_AVTALE:
      return RowBackground.deactivated;
    case RowStatus.AKTIV_IKKE_BESTILLINGSPUNKT:
      return RowBackground.default;
    case RowStatus.AKTIV_BESTILLINGSPUNKT:
      return RowBackground.sky;
  }
};

// TODO Add actual status, when it's decided what it should be
const ModalTableRow: React.FC<React.PropsWithChildren<ModalTableRowProps>> = ({
  sted,
  top,
  collapsed,
  setCollapsed,
  indent,
  checked,
  setChecked,
  headers,
  indeterminate,
  status,
}) => {
  const { canEditAgreement } = useAgreementContext();
  const { id, navn } = sted;
  const { under: undernoder } = sted.noder;
  const { isStudiestedSection } = useStedSectionContext();
  const hasUndernoder = undernoder.length > 0;
  const statusLabel = useStatusFieldLabel(status);

  return (
    <TableRow rowBackground={getTableRowBackground(status)}>
      <TableCellTd
        header={headers[ModalTableHeader.STED]}
        className={css.stedColumn}
      >
        <div className={css.tableCellContent}>
          {top && hasUndernoder && (
            <ArrowToggleButton
              iconType={
                collapsed ? IconType.OpenRowTriangle : IconType.CloseRowTriangle
              }
              onClick={() => setCollapsed(!collapsed, id)}
            />
          )}
          <div
            className={css.studiestedNavn}
            style={{ marginLeft: `${1.5 * (indent || 0) + 0.75}em` }}
          >
            {!hasUndernoder && (
              <Icon className={css.icon} type={IconType.EndNode} />
            )}
            {navn}
          </div>
        </div>
      </TableCellTd>
      {(devEnvFlag || testEnvFlag || !isStudiestedSection) && (
        <>
          <TableCellTd
            header={
              (headers as PraksisstedModalTableHeaders)[ModalTableHeader.STATUS]
            }
            className={css.statusColumn}
          >
            {statusLabel}
          </TableCellTd>
          <TableCellTd
            header={
              (headers as PraksisstedModalTableHeaders)[
                ModalTableHeader.HANDLING
              ]
            }
            align={'right'}
            className={css.actionColumn}
          >
            <Checkbox
              id={`checkbox-for-${id}`}
              checked={checked}
              indeterminate={indeterminate}
              setChecked={() => setChecked && setChecked(id)}
              disabled={!canEditAgreement}
            />
          </TableCellTd>
        </>
      )}
    </TableRow>
  );
};

export default StedSection;
